/**
 * The folder cards above the list.
 *
 * WHAT THIS IS, AND WHAT IT IS NOT. It is a second rendering of the folder
 * navigation the rail already provides — same `category` state, same counts,
 * same vocabulary. It is NOT a new data source: the numbers come from the
 * summary the page already reads once on mount (S4), so a card costs nothing
 * and eleven cards cost nothing. A card that fetched its own count would
 * turn one request into twelve and make the parts able to disagree with the
 * whole.
 *
 * WHY IT DOES NOT REPLACE THE RAIL. Below `xl:` the rail is gone (S7) and
 * the compact select is the folder control; the cards are visible at every
 * width. So on a phone the cards are the browsable view of the taxonomy and
 * the select is the fast one, and above `xl:` the rail carries navigation
 * while the cards carry stock-at-a-glance. Two renderings, one state — the
 * same arrangement S5 established for the table and the card stack.
 *
 * WHY A SUBSET, NOT ALL TWELVE. Eleven categories plus "all" is two full
 * rows of cards before a reader reaches the list, which buries the thing
 * they came for. The cards show the folders that actually hold documents,
 * newest stock first, and the rail (or the select) remains the complete,
 * always-present list — nothing becomes unreachable by being off the grid.
 *
 * ACCESSIBILITY. Each card is a real `<button>` inside a `<nav>` with its
 * own name, so the cards do not present as a second unnamed "Ordner"
 * navigation next to the rail's. `aria-current` marks the active folder, so
 * "which folder am I in" is announced and not merely tinted. The count is
 * in the accessible name, which is why the visible number is `aria-hidden`:
 * a screen reader should hear "Rechnungen, 12 Dokumente" once.
 */
import React from "react";
import { ChevronRight, Folder, Folders } from "lucide-react";

import { categoryAccent } from "./documentCategoryAccent";

/** How many folder cards the grid shows at most, "Alle Dokumente" included. */
export const MAX_CARDS = 8;

/**
 * The cards to render: "all" first, then the fullest folders.
 *
 * Exported so the page's tests can assert the selection rule directly
 * rather than inferring it from rendered markup.
 */
export function pickCards(categories, byCategory, total) {
  const withStock = categories
    .map((o) => ({ value: o.value, label: o.label, count: byCategory[o.value] ?? 0 }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "de"));

  return [
    { value: "", label: "Alle Dokumente", count: total },
    ...withStock,
  ].slice(0, MAX_CARDS);
}

export default function DocumentCategoryCards({
  categories,
  activeCategory,
  onSelect,
  total,
  byCategory,
  countsReady,
}) {
  // Until the summary lands there is nothing honest to show: a grid of "—"
  // would be eight cards of noise above the list. The rail already carries
  // the pending state for the reader who is looking at counts.
  if (!countsReady) return null;

  const cards = pickCards(categories, byCategory, total);
  // A brand-new organisation has one card ("Alle Dokumente", 0) and no
  // folders with stock. One card is not a grid — the empty state below
  // says the same thing better.
  if (cards.length <= 1) return null;

  return (
    <nav aria-label="Ordner-Übersicht" className="mb-5">
      {/* FOUR ACROSS ONLY FROM `2xl:`, and that is a measurement, not a
          preference. The reference puts four cards on a 1440 screen, but it
          gives its main column more room than this shell does: at 1440
          Vantio spends 288px on the global sidebar, 80px on layout padding
          and 240px on the folder rail, leaving 816px for the list column.
          Four cards in 816px are 195px wide — 85px of that is the name —
          and one folder label clips; at 1280 five of eight clip, reading
          "Mietverträge &…". A clipped folder name loses the one thing the
          card is for, so the grid goes three-across until there is room for
          four (measured: 0 clipped at every viewport, 264px cards at 1440). */}
      <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {cards.map(({ value, label, count }) => {
          const active = activeCategory === value;
          const accent = categoryAccent(value);
          const Icon = value === "" ? Folders : Folder;
          return (
            <li key={value || "__all__"}>
              <button
                type="button"
                onClick={() => onSelect(value)}
                aria-current={active ? "true" : undefined}
                aria-label={`${label}, ${count} Dokumente`}
                className={`group flex w-full items-center gap-3 rounded-[12px] border px-3.5 py-3 text-left transition-colors ${
                  active
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card hover:border-primary/40 hover:bg-muted/60"
                }`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border bg-muted ${accent.border}`}
                  aria-hidden="true"
                >
                  <Icon className={`h-5 w-5 ${accent.icon}`} />
                </span>
                <span className="min-w-0 flex-1">
                  {/* Two lines, then clamp: "Marketing & Kommunikation" is a
                      folder name a reader navigates by, so it wraps rather
                      than turning into an ellipsis. */}
                  <span className="m-0 line-clamp-2 break-words text-[13px] font-semibold leading-snug text-foreground">
                    {label}
                  </span>
                  <span
                    className="mt-0.5 block text-[11px] tabular-nums text-muted-foreground"
                    aria-hidden="true"
                  >
                    {count} {count === 1 ? "Dokument" : "Dokumente"}
                  </span>
                </span>
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                  aria-hidden="true"
                />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
